import { useCallback, useState } from "react";
import { imageUrl, imageSrcSet, DEFAULT_SIZES_ATTR } from "@/lib/image";

/**
 * SmartImage — drop-in replacement for <img>:
 *   - aspect-ratio respected via width/height attrs (no CLS)
 *   - srcset for responsive sizes when CDN supports it
 *   - lazy decode + loading
 *   - shimmer background while loading
 *   - rewrites any legacy image URLs to the brand CDN as a safety net
 *     (the backend already does this on every API response).
 */
const CDN = "https://cdn.luxsova.com";
const LEGACY_HOSTS = [
  "https://luxsova.com/wp-content", // WP origin — now offline
];

function normalize(src) {
  if (!src) return src;
  for (const host of LEGACY_HOSTS) {
    if (src.startsWith(host)) {
      // Replace just the host, preserving the /wp-content/... path
      const path = src.slice(host.length);
      // luxsova.com/wp-content keeps wp-content in the path; the others
      // already have a clean key. Both end up under cdn.luxsova.com/<path>.
      return host.includes("luxsova.com/wp-content")
        ? `${CDN}/wp-content${path}`
        : `${CDN}${path}`;
    }
  }
  return src;
}

/** Public helper kept for callers that still import the symbol. */
export const resolveImageSrc = normalize;

const SmartImage = ({
  src,
  alt = "",
  width,
  height,
  sizes = DEFAULT_SIZES_ATTR,
  eager = false,
  className = "",
  style = {},
  ...rest
}) => {
  const [hidden, setHidden] = useState(false);

  const handleError = useCallback((e) => {
    // The WP fallback is gone — if R2/CDN doesn't have the image, hide
    // the broken image icon rather than show a sad placeholder.
    setHidden(true);
    e.currentTarget.style.visibility = "hidden";
  }, []);

  if (!src || hidden) {
    return (
      <div
        className={`flex items-center justify-center bg-neutral-50 text-neutral-300 ${className}`}
        style={{ aspectRatio: width && height ? `${width}/${height}` : "1/1", ...style }}
        aria-hidden="true"
        data-testid="smart-image-fallback"
      >
        {/* Minimal branded placeholder — a tiny SVG so the card never
            renders as a completely blank white block if the CDN misses
            an image. Users still see the tile & can tap it. */}
        <svg
          viewBox="0 0 32 32"
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="4" y="6" width="24" height="20" rx="1" />
          <circle cx="12" cy="14" r="2" />
          <path d="M4 22l7-7 6 6 4-4 7 7" />
        </svg>
      </div>
    );
  }
  const resolved = normalize(src);
  return (
    <img
      src={imageUrl(resolved, width || 600)}
      srcSet={imageSrcSet(resolved)}
      sizes={sizes}
      alt={alt}
      width={width}
      height={height}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      fetchPriority={eager ? "high" : "auto"}
      onError={handleError}
      className={className}
      style={{ backgroundColor: "#ffffff", ...style }}
      {...rest}
    />
  );
};

export default SmartImage;
